import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, ArrowLeft, Printer } from "lucide-react";
import ClaimTracker from "@/components/ClaimTracker";

/**
 * Dedicated single-page print view for one claim.
 * - Fetched on mount via /api/portal/claims/{cid}
 * - Auto-fires window.print() once loaded (user's browser handles Save-as-PDF)
 * - @media print rules strip nav/footer chrome for a clean one-page export
 */
export default function ClaimPrintView() {
  const { cid } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [claim, setClaim] = useState(null);
  const [veteran, setVeteran] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!cid) return;
    Promise.all([
      api.get(`/portal/claims/${cid}`),
      api.get("/portal/me"),
    ])
      .then(([c, m]) => {
        setClaim(c.data);
        setVeteran(m.data?.veteran || null);
      })
      .catch((e) => setErr(e.response?.data?.detail || "Could not load this claim"));
  }, [cid]);

  // Auto-open the print dialog once the render is done
  useEffect(() => {
    if (claim && veteran) {
      const t = setTimeout(() => window.print(), 500);
      return () => clearTimeout(t);
    }
  }, [claim, veteran]);

  if (authLoading) return <div className="grid place-items-center h-96"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!user) return <div className="p-24 text-center">Please sign in.</div>;
  if (err) return (
    <div className="mx-auto max-w-xl px-6 py-24 text-center">
      <h1 className="font-display text-3xl">Cannot load this claim.</h1>
      <p className="mt-3 text-primary/70">{err}</p>
      <Link to="/portal" className="mt-6 inline-flex items-center gap-2 text-primary hover:underline"><ArrowLeft className="w-4 h-4" /> Back to portal</Link>
    </div>
  );
  if (!claim) return <div className="grid place-items-center h-96"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const vetName = [veteran?.first_name, veteran?.last_name].filter(Boolean).join(" ") || veteran?.full_name || user.name || user.email;
  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const updated = claim.updated_at ? new Date(claim.updated_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "";

  return (
    <>
      <style>{`
        @page { size: letter; margin: 0.6in; }
        @media print {
          .print-hide { display: none !important; }
          body { background: #ffffff !important; }
          .print-page { box-shadow: none !important; page-break-inside: avoid; }
        }
      `}</style>

      {/* Screen-only toolbar */}
      <div className="print-hide bg-secondary border-b border-border">
        <div className="mx-auto max-w-3xl px-6 py-4 flex items-center justify-between">
          <Link to="/portal" className="inline-flex items-center gap-2 text-primary hover:underline text-sm" data-testid="print-back">
            <ArrowLeft className="w-4 h-4" /> Back to portal
          </Link>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 h-10 px-4 bg-accent text-accent-foreground font-semibold text-sm hover:bg-accent/90"
            data-testid="print-download-btn"
          >
            <Printer className="w-4 h-4" /> Save as PDF / Print
          </button>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-6 py-10 print-page bg-white" data-testid="claim-print-view">
        {/* Letterhead */}
        <div className="border-b-4 border-primary pb-6 mb-8">
          <div className="flex items-baseline justify-between flex-wrap gap-3">
            <div>
              <div className="font-mono text-xs tracking-[0.25em] text-accent-foreground">VETERANS DISABILITY EDUCATION</div>
              <h1 className="font-display text-3xl mt-1">Claim Timeline</h1>
            </div>
            <div className="text-right text-xs font-mono text-primary/60">
              <div>Generated {today}</div>
              {updated && <div className="mt-1">Last update {updated}</div>}
            </div>
          </div>
        </div>

        {/* Header block */}
        <div className="grid grid-cols-2 gap-6 mb-8 text-sm">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-primary/60 mb-1">Veteran</div>
            <div className="font-display text-xl" data-testid="print-vet-name">{vetName}</div>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-primary/60 mb-1">Claim type</div>
            <div className="font-display text-xl" data-testid="print-claim-type">{claim.claim_type}</div>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-primary/60 mb-1">Current status</div>
            <div className="font-semibold" data-testid="print-current-status">{claim.current_status}</div>
          </div>
          {claim.waiting_on && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-primary/60 mb-1">Waiting on</div>
              <div className="font-semibold">{claim.waiting_on}</div>
            </div>
          )}
          {claim.cp_exam_date && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-primary/60 mb-1">C&P Exam date</div>
              <div className="font-semibold">{claim.cp_exam_date}</div>
            </div>
          )}
          {claim.denial_action && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-primary/60 mb-1">Next action</div>
              <div className="font-semibold">{claim.denial_action}</div>
            </div>
          )}
        </div>

        {claim.status_note && (
          <div className="bg-secondary border-l-4 border-accent p-5 mb-8">
            <div className="font-mono text-[10px] uppercase tracking-widest text-primary/60 mb-2">Note from Pamela</div>
            <p className="text-primary/90 leading-relaxed">{claim.status_note}</p>
          </div>
        )}

        {/* Timeline */}
        <div className="mb-10">
          <ClaimTracker claim={claim} />
        </div>

        {/* Footer disclaimer */}
        <div className="border-t border-border pt-5 mt-10 text-[11px] text-primary/60 leading-relaxed">
          <strong>About this document.</strong> This is an informational timeline generated from your VDE portal. It reflects the current status of your claim as tracked by Pamela and does not create any legal record. Only the U.S. Department of Veterans Affairs issues official claim decisions. VDE provides education, coaching, and strategic guidance only. Nothing on this page is legal or medical advice, and VDE is not affiliated with or endorsed by the VA.
        </div>
        <div className="mt-6 flex items-baseline justify-between text-[11px] font-mono text-primary/50">
          <span>veteransdisabilityeducation.com</span>
          <span>Claim ID: {claim.claim_id}</span>
        </div>
      </div>
    </>
  );
}
